import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type { AncChipSize } from './anc-chip.models';

/**
 * Pill-shaped chip aligned with New Library Figma tokens (node 1973:12106).
 * Replaces ad-hoc `.capsule`/count-badge markup across the app.
 */
@Component({
    selector: 'lib-anc-chip',
    imports: [CommonModule],
    templateUrl: './anc-chip.component.html',
    styleUrls: ['./anc-chip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncChipComponent {
    /** Chip label text. */
    @Input() label = '';

    /** Figma size variant. `default` = 14px label / 5px vertical padding, `compact` = 12px label / 4px vertical padding. */
    @Input() size: AncChipSize = 'default';

    /** Figma `Pressed` state — thicker dark border, used for an active/open/selected chip. */
    @Input() selected = false;

    /** Figma `Disabled` state — greyed out, not interactive. */
    @Input() disabled = false;

    /** Optional leading icon class (e.g. `icon-filter`), rendered at 16x16. */
    @Input() leadingIcon?: string;

    /** Shows a trailing close (`x`) icon and emits `remove` on click. */
    @Input() removable = false;

    /** Accessible label; defaults to `label` when not provided. */
    @Input() ariaLabel?: string;

    /** Emits when the chip body is clicked (ignored while `disabled`). */
    @Output() chipClick = new EventEmitter<void>();

    /** Emits when the trailing close icon is clicked (ignored while `disabled`). */
    @Output() remove = new EventEmitter<void>();

    onClick(): void {
        if (this.disabled) {
            return;
        }

        this.chipClick.emit();
    }

    onRemoveClick(event: Event): void {
        event.stopPropagation();

        if (this.disabled) {
            return;
        }

        this.remove.emit();
    }
}
